/**
 * Query keys for React Query caching
 * Following best practices: https://tkdodo.eu/blog/effective-react-query-keys
 */

import type {
  UserFilters,
  AccessObjectFilters,
  PartnerNetworkFilters,
  PartnerTypeFilters,
  EntityParticipationFilters,
} from '../api';

// RoleFilters used to come from the legacy platform-scoped services/roles.ts.
// That service is removed; platform-scoped role listing is non-functional
// until PR-5 folds /api/roles into the admin plugin.
type RoleFilters = Record<string, unknown>;

export const queryKeys = {
  // Users
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (filters: UserFilters) => [...queryKeys.users.lists(), filters] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
    roles: (id: string) => [...queryKeys.users.detail(id), 'roles'] as const,
    accessAssignments: (id: string) => [...queryKeys.users.detail(id), 'access'] as const,
    permissions: (id: string) => [...queryKeys.users.detail(id), 'permissions'] as const,
  },

  // Companies — see `companyKeys` below; company keys live in that factory.

  // Roles
  roles: {
    all: ['roles'] as const,
    lists: () => [...queryKeys.roles.all, 'list'] as const,
    list: (filters: RoleFilters) => [...queryKeys.roles.lists(), filters] as const,
    details: () => [...queryKeys.roles.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.roles.details(), id] as const,
    system: ['roles', 'system'] as const,
    company: (companyId: string) => ['roles', 'company', companyId] as const,
    users: (id: string) => [...queryKeys.roles.detail(id), 'users'] as const,
    permissions: (id: string) => [...queryKeys.roles.detail(id), 'permissions'] as const,
    stats: (id: string) => [...queryKeys.roles.detail(id), 'stats'] as const,
  },

  // Access Objects
  accessObjects: {
    all: ['accessObjects'] as const,
    lists: () => [...queryKeys.accessObjects.all, 'list'] as const,
    list: (filters: AccessObjectFilters) => [...queryKeys.accessObjects.lists(), filters] as const,
    details: () => [...queryKeys.accessObjects.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.accessObjects.details(), id] as const,
    system: ['accessObjects', 'system'] as const,
    category: (category: string) => ['accessObjects', 'category', category] as const,
    scope: (scope: string) => ['accessObjects', 'scope', scope] as const,
    plugin: (pluginId: string) => ['accessObjects', 'plugin', pluginId] as const,
    roles: (id: string) => [...queryKeys.accessObjects.detail(id), 'roles'] as const,
    users: (id: string) => [...queryKeys.accessObjects.detail(id), 'users'] as const,
    stats: (id: string) => [...queryKeys.accessObjects.detail(id), 'stats'] as const,
  },

  // Partner Networks
  partnerNetworks: {
    all: ['partnerNetworks'] as const,
    lists: () => [...queryKeys.partnerNetworks.all, 'list'] as const,
    list: (filters: PartnerNetworkFilters) =>
      [...queryKeys.partnerNetworks.lists(), filters] as const,
    details: () => [...queryKeys.partnerNetworks.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.partnerNetworks.details(), id] as const,
    company: (companyId: string) => ['partnerNetworks', 'company', companyId] as const,
    asOwner: (companyId: string) => ['partnerNetworks', 'owner', companyId] as const,
    asPartner: (companyId: string) => ['partnerNetworks', 'partner', companyId] as const,
    sharedEntities: (id: string) => [...queryKeys.partnerNetworks.detail(id), 'shared'] as const,
    stats: (id: string) => [...queryKeys.partnerNetworks.detail(id), 'stats'] as const,
    graph: (companyId: string) => ['partnerNetworks', 'graph', companyId] as const,
  },

  // Partner Types
  partnerTypes: {
    all: ['partnerTypes'] as const,
    lists: () => [...queryKeys.partnerTypes.all, 'list'] as const,
    list: (filters: PartnerTypeFilters) => [...queryKeys.partnerTypes.lists(), filters] as const,
    details: () => [...queryKeys.partnerTypes.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.partnerTypes.details(), id] as const,
    system: ['partnerTypes', 'system'] as const,
    company: (companyId: string) => ['partnerTypes', 'company', companyId] as const,
    partnerships: (id: string) => [...queryKeys.partnerTypes.detail(id), 'partnerships'] as const,
    stats: (id: string) => [...queryKeys.partnerTypes.detail(id), 'stats'] as const,
  },

  // Entity Participations
  entityParticipations: {
    all: ['entityParticipations'] as const,
    lists: () => [...queryKeys.entityParticipations.all, 'list'] as const,
    list: (filters: EntityParticipationFilters) =>
      [...queryKeys.entityParticipations.lists(), filters] as const,
    details: () => [...queryKeys.entityParticipations.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.entityParticipations.details(), id] as const,
    byEntity: (entityType: string, entityId: string) =>
      ['entityParticipations', 'entity', entityType, entityId] as const,
    byCompany: (companyId: string, entityType?: string) =>
      ['entityParticipations', 'company', companyId, entityType || 'all'] as const,
    stats: (companyId: string) =>
      [...queryKeys.entityParticipations.all, 'stats', companyId] as const,
  },

  // Feature Flags
  featureFlags: {
    all: ['featureFlags'] as const,
    forCompany: (companyId: string) => ['featureFlags', 'company', companyId] as const,
  },

  // System Stats (platform-wide)
  systemStats: {
    all: ['systemStats'] as const,
  },
};

// ----------------------------------------------------------------------------
// Per-domain key factories (orders/warehouse-style) for company-scoped APIs.
// Coexist with the queryKeys object above; new code should prefer these.
// ----------------------------------------------------------------------------

import type { CompanyQueryParams, VisibleCompanyQueryParams } from '../api/companies';
import type { MembershipQueryParams } from '../api/memberships';
import type { InvitationQueryParams } from '../api/invitations';
import type { PartnershipQueryParams } from '../api/partnerships';

export const companyKeys = {
  all: ['companies'] as const,
  lists: () => [...companyKeys.all, 'list'] as const,
  list: (params?: CompanyQueryParams) => [...companyKeys.lists(), params] as const,
  visible: () => [...companyKeys.all, 'visible'] as const,
  visibleList: (params?: VisibleCompanyQueryParams) => [...companyKeys.visible(), params] as const,
  roots: () => [...companyKeys.all, 'roots'] as const,
  details: () => [...companyKeys.all, 'detail'] as const,
  detail: (id: string) => [...companyKeys.details(), id] as const,
  bySlug: (slug: string) => [...companyKeys.all, 'slug', slug] as const,
  hierarchy: (id: string) => [...companyKeys.all, id, 'hierarchy'] as const,
  ancestors: (id: string) => [...companyKeys.all, id, 'ancestors'] as const,
  descendants: (id: string) => [...companyKeys.all, id, 'descendants'] as const,
  children: (id: string) => [...companyKeys.all, id, 'children'] as const,
};

export const roleKeys = {
  all: ['company-roles'] as const,
  lists: (companyId: string) => [...roleKeys.all, 'list', companyId] as const,
  list: (companyId: string) => [...roleKeys.lists(companyId)] as const,
  details: () => [...roleKeys.all, 'detail'] as const,
  detail: (companyId: string, roleId: string) =>
    [...roleKeys.details(), companyId, roleId] as const,
};

export const membershipKeys = {
  all: ['company-memberships'] as const,
  lists: (companyId: string) =>
    [...membershipKeys.all, 'list', companyId] as const,
  list: (companyId: string, params?: MembershipQueryParams) =>
    [...membershipKeys.lists(companyId), params] as const,
  details: () => [...membershipKeys.all, 'detail'] as const,
  detail: (companyId: string, memberId: string) =>
    [...membershipKeys.details(), companyId, memberId] as const,
};

export const invitationKeys = {
  all: ['company-invitations'] as const,
  lists: (companyId: string) =>
    [...invitationKeys.all, 'list', companyId] as const,
  list: (companyId: string, params?: InvitationQueryParams) =>
    [...invitationKeys.lists(companyId), params] as const,
  details: () => [...invitationKeys.all, 'detail'] as const,
  detail: (companyId: string, invitationId: string) =>
    [...invitationKeys.details(), companyId, invitationId] as const,
};

export const partnershipKeys = {
  all: ['company-partnerships'] as const,
  lists: (companyId: string) =>
    [...partnershipKeys.all, 'list', companyId] as const,
  list: (companyId: string, params?: PartnershipQueryParams) =>
    [...partnershipKeys.lists(companyId), params] as const,
  details: () => [...partnershipKeys.all, 'detail'] as const,
  detail: (companyId: string, partnershipId: string) =>
    [...partnershipKeys.details(), companyId, partnershipId] as const,
};

export const pluginKeys = {
  all: ['plugins'] as const,
  catalog: () => [...pluginKeys.all, 'catalog'] as const,
};
